import AngleLeftSvg from "@icons/angle-left.svg?react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useSuspenseQuery } from "@tanstack/react-query";
import { isNumber } from "lodash-es";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { z } from "zod";

import {
  cropUnitSchema,
  traderSchema as baseTraderSchema,
} from "@/api/types/digitalnisklady.cz/types";
import { BindingOfferMarketplaceOrderAllTradersDialog } from "@/common/components/binding-offer-dialog/binding-offer-marketplace-order-all-traders-dialog";
import { BindingOfferMarketplaceOrderDialog } from "@/common/components/binding-offer-dialog/binding-offer-marketplace-order-dialog";
import { Button } from "@/common/components/button/button";
import { Callout } from "@/common/components/callout/callout";
import { InputField } from "@/common/components/form/input-field/input-field";
import {
  trackBetterOfferCancelled,
  trackBetterOfferSubmitted,
} from "@/common/components/mixpanel/mixpanel-tracking";
import { PropertiesList } from "@/common/components/properties-list/properties-list";
import { Spacer } from "@/common/components/spacer/spacer";
import { useCurrency } from "@/common/hooks/use-currency";
import { useRequiredErpCompany } from "@/common/hooks/use-erp-company";
import { useFormConfirmation } from "@/common/hooks/use-form-confirmation";
import { entitySchema } from "@/common/schema/entity";
import {
  currencyFormatter,
  numberFormatter,
} from "@/common/services/formatter";
import { getHarvestYearFromQuarter } from "@/common/utils/trading";
import { Currency } from "@/generated/digitalnisklady.cz/graphql";
import { FullscreenModalLayout } from "@/routes/layout/layout.fullscreen";
import { getUnit } from "@/routes/shared/utils/crop-unit";
import { futureQuartersQuery } from "@/routes/trades/trades.api";

import { useAddFastOrderMutation } from "../../quick-trading/quick-trading.detail.api";
import { useGoToFailed } from "../../shared/routes/failed/failed";
import { path as successPath } from "../../shared/routes/success/success";

type Fields = {
  quarter: string;
  pricePerUnit: number;
  trader: string;
  amount: number;
  note: string;
};

const MarketplaceDetailOrder = () => {
  const context = useRouteContext();
  const company = useRequiredErpCompany();
  const unit = getUnit(context.trader.unit);
  const [, currency] = useCurrency();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const goToFailedScreen = useGoToFailed();
  const addFastOrder = useAddFastOrderMutation();
  const quarters = useSuspenseQuery(futureQuartersQuery());
  const close = useClose();

  const {
    postalCodeLabel,
    trader: { minimumAmount },
  } = context;
  const formattedMinimumAmount = isNumber(minimumAmount)
    ? numberFormatter().format(minimumAmount)
    : 0;

  const form = useForm<Fields>({
    defaultValues: {
      pricePerUnit: context.price,
      quarter: context.quarter?.id,
      trader: "ALL",
    },
  });

  const { confirm, dialog, formData, rejectRef, resolveRef } =
    useFormConfirmation<Fields>();

  const handleSubmit = (data: Fields) => {
    const selectedQuarter = quarters.data.find((q) => q.id === data.quarter);
    const harvestYear = selectedQuarter
      ? getHarvestYearFromQuarter(selectedQuarter.name)
      : new Date().getFullYear();

    trackBetterOfferSubmitted({
      crop: context.item.name,
      currency,
      id: context.item.id,
      price: data.pricePerUnit,
      quarter: selectedQuarter?.name ?? "",
    });

    addFastOrder.mutate(
      {
        amount: data.amount,
        companyId: company.id,
        cropId: context.item.id,
        currency: currency as Currency,
        gps: {
          latitude: context.latitude,
          longitude: context.longitude,
        },
        harvestYear,
        lowPrice: null,
        note: data.note,
        price: data.pricePerUnit,
        quarterId: data.quarter,
        zip: context.postalCode,
        ...(data.trader === "ALL" ? {} : { onlyTrader: data.trader }),
      },
      {
        onError: goToFailedScreen,
        onSuccess: async (data) => {
          await navigate(
            `${data.addFastOrder?.id}/offer/${data.addFastOrder?.offer?.id}/${successPath}?${searchParams.toString()}`,
          );
        },
      },
    );
  };

  const handleFormSubmit = (data: Fields) => {
    confirm(<Trans>Přejete si odeslat poptávku?</Trans>, data, handleSubmit);
  };

  const selectedTrader =
    formData?.trader && formData.trader !== "ALL"
      ? context.traderPrices.find((tp) => tp.trader.id === formData.trader)
          ?.trader
      : undefined;
  const selectedQuarterName =
    quarters.data.find((q) => q.id === formData?.quarter)?.name ??
    context.quarter?.name ??
    "";

  return (
    <>
      <FullscreenModalLayout
        headerButton={
          <button onClick={close}>
            <AngleLeftSvg />
          </button>
        }
      >
        <FullscreenModalLayout.Title>
          <Trans>Mám lepší nabídku</Trans>
        </FullscreenModalLayout.Title>
        <PropertiesList
          properties={[
            {
              label: t`Název`,
              value: context.item.name,
            },
            {
              label: t`PSČ`,
              value: postalCodeLabel,
            },
          ]}
        />
        <Spacer />
        <Callout>
          <p className="text-base">
            <Trans>
              Máte konkrétní cenu od jiného dodavatele? Vyplňte množství a
              konkurenční cenu. Obchodník váš požadavek individuálně posoudí a
              prověří možnost nákupu za těchto podmínek.
            </Trans>
          </p>
        </Callout>
        <Spacer />
        <FormProvider {...form}>
          <form
            className="flex flex-col gap-y-6"
            id="marketplace-detail-order"
            onSubmit={form.handleSubmit(handleFormSubmit)}
          >
            <label className="group" htmlFor="quarter">
              <Trans>Kvartál</Trans>
              <select
                className="group-has-[[role=alert]]:border-can-russet mt-2 h-[calc(--spacing(14)+2px)] w-full rounded-lg p-4 font-bold"
                id="quarter"
                {...form.register("quarter", {
                  required: t`Vyberte prosím kvartál`,
                })}
              >
                {quarters.data.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.name}
                  </option>
                ))}
              </select>
            </label>
            <InputField<Fields>
              label={t`Cena za ${unit}`}
              name="pricePerUnit"
              options={{
                min: {
                  message: t`Cena musí být kladná`,
                  value: 0.01,
                },
                required: t`Vyplňte prosím cenu`,
                validate: (value) =>
                  Number(value) < context.price ||
                  t`Cena musí být nižší než aktuální nabídka`,
                valueAsNumber: true,
              }}
              step="0.01"
              type="number"
            />
            <label htmlFor="trader">
              <Trans>Obchodník</Trans>
              <select
                className="mt-2 w-full rounded-lg p-4"
                id="trader"
                {...form.register("trader", {
                  required: t`Vyberte prosím obchodníka`,
                })}
              >
                <option value="ALL">
                  <Trans>Všichni obchodníci</Trans>
                </option>
                {context.traderPrices.map((tp) => (
                  <option key={tp.trader.id} value={tp.trader.id}>
                    {tp.trader.name}
                  </option>
                ))}
              </select>
            </label>
            <InputField<Fields>
              label={t`Množství` + " (" + unit + ")"}
              name="amount"
              options={{
                min: {
                  message: t`Množství musí být vyšší než ${formattedMinimumAmount} ${unit}`,
                  value: minimumAmount ?? 0,
                },
                required: t`Vyplňte prosím množství`,
                valueAsNumber: true,
              }}
              type="number"
            />
            <InputField<Fields> label={t`Poznámka`} name="note" />
          </form>
        </FormProvider>
        <Button.Container>
          <Button
            form="marketplace-detail-order"
            type="submit"
            variant="primary"
          >
            <Trans>Odeslat poptávku</Trans>
          </Button>
        </Button.Container>
      </FullscreenModalLayout>
      <Outlet />
      {dialog && formData ? (
        formData.trader === "ALL" ? (
          <BindingOfferMarketplaceOrderAllTradersDialog
            amount={formData.amount + " " + unit}
            cropName={context.item.name}
            isPending={addFastOrder.status === "pending"}
            onCancel={() => {
              trackBetterOfferCancelled({
                crop: context.item.name,
                id: context.item.id,
              });
              rejectRef.current?.();
            }}
            onConfirm={(e) => resolveRef.current?.(e)}
            postalCodeLabel={postalCodeLabel}
            quarter={selectedQuarterName}
            unitPrice={currencyFormatter(currency).format(
              formData.pricePerUnit,
            )}
          />
        ) : (
          <BindingOfferMarketplaceOrderDialog
            amount={formData.amount + " " + unit}
            cropName={context.item.name}
            isPending={addFastOrder.status === "pending"}
            onCancel={() => {
              trackBetterOfferCancelled({
                crop: context.item.name,
                id: context.item.id,
              });
              rejectRef.current?.();
            }}
            onConfirm={(e) => resolveRef.current?.(e)}
            postalCodeLabel={postalCodeLabel}
            quarter={selectedQuarterName}
            timeFromTrading={selectedTrader?.timeFromTrading ?? null}
            timeToTrading={selectedTrader?.timeToTrading ?? null}
            tocUrl={selectedTrader?.tocUrl ?? null}
            traderName={selectedTrader?.name ?? ""}
            unitPrice={currencyFormatter(currency).format(
              formData.pricePerUnit,
            )}
          />
        )
      ) : null}
    </>
  );
};

const useClose = () => {
  const navigate = useNavigate();
  const { search } = useLocation();

  return async () => {
    await navigate(
      {
        pathname: "..",
        search,
      },
      {
        relative: "path",
      },
    );
  };
};

const traderSchema = baseTraderSchema.extend(
  z.object({
    minimumAmount: z.number().optional().nullable(),
    unit: cropUnitSchema,
  }).shape,
);

const traderPriceSchema = z.object({
  id: z.string(),
  price: z.number(),
  quarter: entitySchema,
  trader: traderSchema,
});

const itemSchema = entitySchema.extend(
  z.object({
    parameters: z.array(
      z.object({
        label: z.string(),
        value: z.string().or(z.number()).or(z.custom<React.ReactElement>()),
      }),
    ),
  }).shape,
);

const contextSchema = z.object({
  item: itemSchema,
  latitude: z.number(),
  longitude: z.number(),
  postalCode: z.string(),
  postalCodeLabel: z.string(),
  price: z.number(),
  quarter: entitySchema.optional(),
  trader: traderSchema,
  traderPrices: z.array(traderPriceSchema),
});

const useRouteContext = () => {
  const context = useOutletContext();

  return contextSchema.parse(context);
};

export { MarketplaceDetailOrder };
